import { lazy } from 'react';
import { type ShouldRevalidateFunctionArgs, useLocation } from 'react-router';

import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import type { LoaderDataWithOverrideLocale } from '#/horizon/utils/getMatchesOverrideLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { cmsApiServerFetchPage } from '#/modules/cms/api/cmsApiFetchPage';
import { getCmsSearchLandingPagePageLayoutHandleProps } from '#/modules/cms/helpers/getCmsSearchLandingPagePageLayoutHandleProps';
import { CmsPagePage } from '#/modules/cms/pages/Page/CmsPagePage';
import { getCmsPagePageLayoutHandleProps } from '#/modules/cms/pages/Page/getCmsPagePageLayoutHandleProps';
import { CmsPostPage } from '#/modules/cms/pages/Post/CmsPostPage';
import { getCmsPagePostLayoutHandleProps } from '#/modules/cms/pages/Post/getCmsPagePostLayoutHandleProps';
import { cmsSplatRoute } from '#/modules/cms/routing/cms.routes';
import { getPostSearchPageLayoutHandleProps } from '#/modules/postSearch/pages/PostSearch/getPostSearchPageLayoutHandleProps';
import { postSearchRouteReference } from '#/modules/postSearch/routing/postSearch.routes';
import { searchGetRoutePathSwitch } from '#/modules/search/routing/helpers/searchGetRoutePathSwitch';
import { LazyNotFoundPage } from '#/pages/NotFound/LazyNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getAppRouteSearchParams } from '#/routing/utils/getAppRouteSearchParams';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { idealistRedirect } from '#/routing/utils/idealistRedirect';
import { imageUrlForOgMeta } from '#/utils/filestack';

import type { CmsSplatPageLoaderData } from './CmsSplatPageLoaderData';
import { getCmsContentOgImage } from './getCmsContentOgImage';
import { idealistOrganizationSchemaMarkup } from './idealistOrganizationSchemaMarkup';

const LazySearchRefinementsSwitch = lazy(() =>
  import('#/modules/search/components/Refinements/SearchRefinementsSwitch').then(
    (module) => ({ default: module.SearchRefinementsSwitch }),
  ),
);

const LazyPostSearchPage = lazy(() =>
  import('#/modules/postSearch/pages/PostSearch/PostSearchPage').then((m) => ({
    default: m.PostSearchPage,
  })),
);

const LazySearchLandingPage = lazy(() =>
  import('#/modules/search/pages/Landing/SearchLandingPage').then((m) => ({
    default: m.SearchLandingPage,
  })),
);

export const handle = {
  searchRefinements: <LazySearchRefinementsSwitch />,
} satisfies LayoutHandleProps;

export function shouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  if (
    // If in the same pathname
    currentUrl.pathname === nextUrl.pathname &&
    // and not adding or removing pq
    currentUrl.searchParams.has('pq') === nextUrl.searchParams.has('pq') &&
    // But changed some other search param (such as tile grid)
    currentUrl.search !== nextUrl.search
  ) {
    // Skip revalidation
    return false;
  }

  return defaultShouldRevalidate;
}

export async function loader({
  request,
  url,
  context,
  params: { ['*']: slug },
}: HorizonAppRouteLoaderArgs<typeof cmsSplatRoute>) {
  const { serverContext } = getServerRouterContext(context);

  const searchParams = getAppRouteSearchParams({
    url,
    route: cmsSplatRoute,
  });

  try {
    const { data: content, ssrLandingPageResults } =
      await cmsApiServerFetchPage({
        request,
        context,
      })({
        slug,
        preview: searchParams.preview === 'true',
        lang: serverContext.locale,
      });

    if (content.type === 'CMS_REDIRECT') {
      return idealistRedirect({ fromUrl: url, context, to: content.url });
    }

    if (content.type === 'SEARCH_LANDING_PAGE') {
      return {
        type: 'SEARCH_LANDING_PAGE',
        content,
        results: ssrLandingPageResults,
        handle: getCmsSearchLandingPagePageLayoutHandleProps({ content }),
        overrideLocale: content.locale,
        now: serverContext.now,
      } satisfies CmsSplatPageLoaderData & LoaderDataWithOverrideLocale;
    }

    if (typeof searchParams['pq'] !== 'undefined' && content.subsite.homePage) {
      return {
        type: 'POST_SEARCH',
        content,
        handle: getPostSearchPageLayoutHandleProps({ content }),
        overrideLocale: content.locale,
      } satisfies CmsSplatPageLoaderData & LoaderDataWithOverrideLocale;
    }

    if (content.heroSearch?.onlyResults) {
      const redirectPath = searchGetRoutePathSwitch({
        q: typeof searchParams.q === 'string' ? searchParams.q : undefined,
        locale: serverContext.locale,
        filtersByName: content.heroSearch.defaultSearchFilters,
        embedSearchWidgetId: null,
      });

      return idealistRedirect({ fromUrl: url, context, to: redirectPath });
    }

    if (content.onlySearchResults && content.subsite?.homePage) {
      const redirectPath = postSearchRouteReference.with({
        pq: typeof searchParams.pq === 'string' ? searchParams.pq : '',
        slug: content.url,
      });

      return idealistRedirect({ fromUrl: url, context, to: redirectPath });
    }

    if (content.type === 'PAGE') {
      return {
        type: 'PAGE',
        content,
        handle: getCmsPagePageLayoutHandleProps({ content }),
        overrideLocale: content.locale,
      } satisfies CmsSplatPageLoaderData & LoaderDataWithOverrideLocale;
    }

    // content.type === 'POST'
    return {
      type: 'POST',
      content,
      handle: getCmsPagePostLayoutHandleProps({ content }),
      overrideLocale: content.locale,
    } satisfies CmsSplatPageLoaderData & LoaderDataWithOverrideLocale;
  } catch {
    return idealistNotFound(null);
  }
}

export default function CmsSplatHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const location = useLocation();

  switch (loaderData?.type) {
    case undefined:
      return <LazyNotFoundPage />;
    case 'PAGE': {
      const image = getCmsContentOgImage(loaderData.content);

      return (
        <RouteModule
          title={loaderData.content.headTitle ?? loaderData.content.title}
          ogImage={
            image
              ? {
                  url: imageUrlForOgMeta({ data: image }),
                  alt: image.altText ?? null,
                }
              : null
          }
          description={loaderData.content.metaDescription}
          robotsNoIndex={!loaderData.content.visibleToRobots}
          linkRels={
            loaderData.content.isPublished
              ? {
                  rssFeed:
                    loaderData.content.subsite.homePage?.id ===
                    loaderData.content.id
                      ? {
                          title: `${loaderData.content.headTitle ?? loaderData.content.title} Feed`,
                          href: `${loaderData.content.url}/feed.rss`,
                        }
                      : null,
                  translations: [
                    loaderData.content,
                    ...loaderData.content.translations,
                  ].map((translation) => ({
                    url: translation.url,
                    locale: translation.locale,
                  })),
                  canonical: loaderData.content.url,
                }
              : null
          }
          schemaMarkups={
            location.pathname === '/en'
              ? [idealistOrganizationSchemaMarkup]
              : undefined
          }
        >
          <CmsPagePage content={loaderData.content} />
        </RouteModule>
      );
    }
    case 'POST': {
      const image = getCmsContentOgImage(loaderData.content);

      return (
        <RouteModule
          title={loaderData.content.headTitle ?? loaderData.content.title}
          ogImage={
            image
              ? {
                  url: imageUrlForOgMeta({ data: image }),
                  alt: image.altText ?? null,
                }
              : null
          }
          description={loaderData.content.metaDescription}
          robotsNoIndex={!loaderData.content.visibleToRobots}
          article={{
            firstPublished: loaderData.content.articleMeta.firstPublished,
            lastModified: loaderData.content.articleMeta.lastModified,
            author: loaderData.content.author?.name ?? null,
            tags: loaderData.content.tags.map((tag) => tag.tag),
          }}
          linkRels={
            loaderData.content.isPublished
              ? {
                  rssFeed:
                    loaderData.content.subsite.homePage?.id ===
                    loaderData.content.id
                      ? {
                          title: `${loaderData.content.headTitle ?? loaderData.content.title} Feed`,
                          href: `${loaderData.content.url}/feed.rss`,
                        }
                      : null,
                  translations: [
                    loaderData.content,
                    ...loaderData.content.translations,
                  ].map((translation) => ({
                    url: translation.url,
                    locale: translation.locale,
                  })),
                  canonical: loaderData.content.url,
                }
              : null
          }
          schemaMarkups={[loaderData.content.schemaMarkup]}
        >
          <CmsPostPage content={loaderData.content} />
        </RouteModule>
      );
    }
    case 'SEARCH_LANDING_PAGE':
      return (
        <RouteModule
          title={
            loaderData.content.location?.name
              ? `${loaderData.content.name}, ${loaderData.content.location.name}`
              : loaderData.content.name
          }
          description={loaderData.content.description}
          linkRels={{
            translations: loaderData.content.translations.map(
              (translation) => ({
                url: translation.url,
                locale: translation.locale,
              }),
            ),
            canonical: location.pathname,
          }}
        >
          <LazySearchLandingPage
            content={loaderData.content}
            now={loaderData.now}
          />
        </RouteModule>
      );
    case 'POST_SEARCH':
      return (
        <RouteModule
          title={loaderData.content.headTitle ?? loaderData.content.title}
          description={loaderData.content.metaDescription}
        >
          <LazyPostSearchPage content={loaderData.content} />
        </RouteModule>
      );
  }
}
